import PropTypes from 'prop-types';
import ReactApexChart from 'react-apexcharts';
// @mui
import { useTheme, styled } from '@mui/material/styles';
import { Card, CardHeader, Box, Skeleton, Alert } from '@mui/material';
// utils
import { fNumber } from '../../../utils/formatNumber';
// components
import { useChart } from '../../../components/chart';

// ----------------------------------------------------------------------

const CHART_HEIGHT = 372;

const StyledChartWrapper = styled('div')(({ theme }) => ({
  height: CHART_HEIGHT,
  marginTop: theme.spacing(5),
  '& .apexcharts-canvas svg': { height: CHART_HEIGHT },
  '& .apexcharts-canvas svg,.apexcharts-canvas foreignObject': {
    overflow: 'visible',
  }
}));

// ----------------------------------------------------------------------

AppCurrentVisits.propTypes = {
  title: PropTypes.string,
  subheader: PropTypes.string,
  chartColors: PropTypes.arrayOf(PropTypes.string),
  chartData: PropTypes.array,
  isLoading: PropTypes.bool,
  error: PropTypes.string,
};

const LoadingSkeleton = () => (
  <Card>
    <CardHeader title="Loading..." />
    <Box sx={{ p: 3 }}>
      <Skeleton variant="rectangular" height={280} />
    </Box>
  </Card>
);

const EmptyState = ({ title }) => (
  <Card>
    <CardHeader title={title} />
    <Box sx={{ p: 4, textAlign: 'center' }}>
      <Box
        sx={{
          width: 80,
          height: 80,
          mx: 'auto',
          mb: 2,
          borderRadius: '50%',
          bgcolor: (theme) => theme.palette.grey[100],
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'text.disabled',
        }}
      >
        <Box
          component="span"
          sx={{
            fontSize: '2rem',
            lineHeight: 1,
          }}
        >
          📊
        </Box>
      </Box>
      <Box sx={{ color: 'text.secondary', typography: 'body2' }}>
        No data available for this chart
      </Box>
    </Box>
  </Card>
);

export default function AppCurrentVisits({ 
  title, 
  subheader, 
  chartColors, 
  chartData, 
  isLoading,
  error,
  ...other 
}) {
  const theme = useTheme();

  // Prepare chart data early to avoid conditional hook calls
  const chartLabels = chartData?.map((i) => i.label) || [];
  const chartSeries = chartData?.map((i) => i.value) || [];

  // Call useChart hook unconditionally at the top level
  const chartOptions = useChart({
    colors: chartColors || [],
    labels: chartLabels,
    stroke: { colors: [theme.palette.background.paper] },
    legend: { 
      floating: true, 
      horizontalAlign: 'center',
      position: 'bottom',
      fontSize: '14px',
      fontFamily: theme.typography.fontFamily,
    },
    dataLabels: { 
      enabled: true, 
      dropShadow: { enabled: false },
      style: {
        fontSize: '12px',
        fontFamily: theme.typography.fontFamily,
      }
    },
    tooltip: {
      fillSeriesColor: false,
      theme: theme.palette.mode,
      style: {
        fontSize: '12px',
        fontFamily: theme.typography.fontFamily,
      },
      y: {
        formatter: (seriesName) => fNumber(seriesName),
        title: {
          formatter: (seriesName) => `${seriesName}`,
        },
      },
    },
    plotOptions: {
      pie: { 
        donut: { 
          labels: { 
            show: false 
          },
          size: '65%',
        } 
      },
    },
    states: {
      hover: {
        filter: {
          type: 'darken',
          value: 0.1,
        },
      },
    },
  });

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  if (error) {
    return (
      <Card {...other}>
        <CardHeader title={title} subheader={subheader} />
        <Alert severity="error" sx={{ m: 2 }}>
          {error}
        </Alert>
      </Card>
    );
  }

  if (!chartData || chartData.length === 0) {
    return <EmptyState title={title} />;
  }

  return (
    <Card {...other}>
      <CardHeader 
        title={title} 
        subheader={subheader}
        sx={{
          '& .MuiCardHeader-title': {
            fontSize: '1.125rem',
            fontWeight: 600,
          },
          '& .MuiCardHeader-subheader': {
            fontSize: '0.875rem',
            color: 'text.secondary',
          },
        }}
      />

      <StyledChartWrapper dir="ltr">
        <ReactApexChart 
          type="donut" 
          series={chartSeries} 
          options={chartOptions} 
          height={280} 
        />
      </StyledChartWrapper>
    </Card>
  );
}
